import { Component, Vue } from 'vue-property-decorator'
import { sessionStore } from '@/store/session/SessionStoreModule'
import { projectStore } from '@/store/session/ProjectStoreModule'
import ShouninTaskStoreModule, { shouninTaskStore } from '@/store/session/ShouninTaskStoreModule'
import CodeLabelStoreModule, { codeLabelStore } from '@/store/session/CodeLabelStoreModule'
import { ApiAgent } from '@/plugins/ApiRequest'
import * as DateUtil from '@/tcw/utils/DateUtil'
import * as TcwService from '@/tcw/utils/TcwService'
import { TCWSessionError, TCWBaseError, TCWUnexpectedError } from '@/plugins/TCWBaseError'
import Getsudo from '@/tcw/domain/Getsudo'
import { TcwAPIID } from '../domain/types'
import Shimebi from '@/tcw/domain/Shimebi'
import { Project } from '@/store/types'
import { DEFAULT_SHIMEBI } from '../constant/TcwConstant'

Component.registerHooks(['beforeRouteEnter', 'beforeRouteLeave', 'beforeRouteUpdate'])

/**
 * コンポーネント基底クラス
 */
@Component
export default class BaseComponent extends Vue {
  protected isLoading = false

  get du() {
    return DateUtil
  }
  get tcw() {
    return TcwService
  }

  get codeLabel(): CodeLabelStoreModule {
    return codeLabelStore
  }
  get project(): Project | null {
    return projectStore.editData
  }
  get hasProject(): boolean {
    return projectStore.isNewRecord === false
  }
  get loginUserId(): TcwAPIID {
    const id = sessionStore.user?.id as TcwAPIID
    return id ? id : ''
  }
  get shouninTask(): ShouninTaskStoreModule {
    return shouninTaskStore
  }
  /**
   * 初回ログインフロー
   */
  get isFirstLoginFlow(): boolean {
    return !this.hasProject
  }

  get shimebi(): Shimebi {
    return Shimebi.generateByProject(this.project)
  }

  get env(): string {
    return process.env.NODE_ENV as string
  }

  get currentDate(): Date {
    return this.$tcw.$store.basic.targetDate
  }

  get currentGetsudo(): Getsudo {
    return Getsudo.generateByDate(this.currentDate, this.shimebi)
  }

  /**
   * セッション存続確認
   */
  protected async restoreSession(): Promise<boolean> {
    // トークンを保持しているか
    if (!this.$cookies.isKey('_ctkn')) {
      return false
    }

    // トークンが保持されており、セッションがストアに格納されていない場合はサーバから取得する
    try {
      this.isLoading = true
      await this.$tcw.session.fetch()
      console.log('セッションデータ再取得成功')
      return true
    } catch (e) {
      console.error('セッションデータ再取得失敗')
      if (e instanceof TCWSessionError) {
        return false
      } else {
        throw e
      }
    } finally {
      this.isLoading = false
    }
  }

  protected getToken(): string {
    return this.$cookies.get('_ctkn')
  }
  protected hasToken(): boolean {
    return this.$cookies.isKey('_ctkn')
  }
  protected clearToken(): void {
    this.$cookies.remove('_ctkn')
  }
  protected countShouninTask(): number | string {
    return this.shouninTask?.count ? this.shouninTask.count : ''
  }

  protected get isMobile(): boolean {
    return this.$mq === 'sp'
  }

  /**
   * セッション情報がストアに格納されているかチェック
   */
  protected checkSession(): Promise<string> {
    if (sessionStore.has && this.$cookies.isKey('_ctkn')) {
      const token = this.getToken()
      return Promise.resolve(token)
    } else {
      const e = new TCWSessionError('セッション切断')
      return Promise.reject(e)
    }
  }

  protected getWebApi(): ApiAgent {
    const token = this.getToken()
    return ApiAgent.create(this.tcw.getApiUrlBase(), token)
  }

  protected throwError(error: unknown): void {
    if (error instanceof TCWBaseError) {
      this.$tcw.$store.errors.setError(error)
    } else if (error instanceof Error) {
      this.$tcw.$store.errors.setError(
        new TCWUnexpectedError('予期せぬエラーが発生しました', error)
      )
    } else {
      this.$tcw.$store.errors.setError(new TCWUnexpectedError('予期せぬエラーが発生しました'))
    }
  }

  protected goto(page: string): void {
    this.$router.push(page)
  }

  protected back(): void {
    this.$router.back()
  }

  protected getGetsudoByDate(date: Date): Date | null {
    const shimebi = this.project ? this.project.shimebi : DEFAULT_SHIMEBI
    return this.tcw.getGetsudo(date, shimebi)
  }

  protected async reloadApp(): Promise<void> {
    this.isLoading = true
    // 最新バージョンのハッシュ値を取得
    const newHash = await this.checkUpdateApp()
    // ハッシュ値をCookieに設定
    this.$cookies.set('app_hash', newHash, -1) // ハッシュ値は必ず設定されている前提とするため、期限なしを設定
    this.$tcw.$store.basic.setUpdateState(false)
    // アプリリロード TODO 非推奨を対応
    location.reload()
  }

  /**
   * アプリの更新有無をチェックする。aaa
   * 更新がある場合、新しい更新チェック用ハッシュ値を返す。
   */
  protected async checkUpdateApp(): Promise<string | null> {
    // サーバからIndex.html取得
    const indexHtml = await this.fetchIndexHtml()

    // ハッシュ値計算
    const serverHash = this.tcw.createHash(indexHtml)

    // 更新チェック用ハッシュ値がCookieに設定されているかチェック
    if (!this.$cookies.isKey('app_hash')) {
      // 設定されていない場合はそのままハッシュを返す
      console.log(`更新チェックハッシュ値が未設定です`)
      return serverHash
    }

    // ハッシュ値をCookieから取得
    const hash = this.$cookies.get('app_hash')
    console.log(`更新チェックハッシュ値: ${hash}`)

    // ハッシュ値比較
    if (hash !== serverHash) {
      // 差異がある場合は新ハッシュ値を返す
      return serverHash
    } else {
      // 差異がない場合はnullを返す
      return null
    }
  }

  private async fetchIndexHtml(): Promise<string> {
    const apiAgent = new ApiAgent('/')
    try {
      const html = await apiAgent.call<string>('', 'GET')
      console.log('サーバからindex.html取得に成功')
      return html
    } catch (e) {
      console.log('サーバからindex.html取得に失敗', e)
      return ''
    }
  }

  private openHelpPage(helpPageTag: string): void {
    const url = this.tcw.getHelpUrlByTag(helpPageTag)
    window.open(url, '_blank')
  }

  private throwDummyError(): void {
    try {
      alert('エラーを発生させます')
      const dummyStr = '関数じゃないけど関数として実行'
      /* @ts-ignore */
      dummyStr()
    } catch (e) {
      if (e instanceof Error) {
        this.throwError(new TCWUnexpectedError('検証用のエラーです', e))
      } else {
        this.throwError(new TCWUnexpectedError('検証用のエラーです'))
      }
    }
  }
}
